import {
	applyMenuConfigToDom,
	buildLanguageRedirectPath,
	MENU_LANGUAGES,
	normalizeMenuLanguage,
	readMenuConfig,
	updateMenuConfig,
	type MenuConfig,
	type MenuFontFamily,
	type ThemeMode,
} from "./menu_config";

/**
 * Menu popup UI module.
 *
 * Uses the canonical MenuConfig in localStorage via [`readMenuConfig()`](src/main_script/menu_config.ts:183)
 * and [`updateMenuConfig()`](src/main_script/menu_config.ts:215).
 *
 * Note: language dropdown is a custom list (array-backed) instead of `<datalist>`,
 * since datalist dropdown UX can be inconsistent across browsers.
 */
export type MenuPopupController = {
	toggle(): void;
	close(): void;
	isOpen(): boolean;
};

export function createMenuPopupController(params: {
	getMenuButton: () => HTMLButtonElement | null;
	applyTheme: (theme: ThemeMode) => void;
}): MenuPopupController {
	const { getMenuButton, applyTheme } = params;
	const LANGUAGE_DROPDOWN_OPEN_CLASS = "is-language-dropdown-open";

	let popup: HTMLDivElement | null = null;
	let open = false;
	let restoreFocus: HTMLElement | null = null;

	let languageInput: HTMLInputElement | null = null;
	let languageList: HTMLDivElement | null = null;

	let themeButtons: Record<ThemeMode, HTMLButtonElement> | null = null;
	let fontButtons: Record<MenuFontFamily, HTMLButtonElement> | null = null;

	const setActiveToggleButton = (btn: HTMLButtonElement, isActive: boolean) => {
		btn.classList.toggle("is-active", isActive);
		btn.setAttribute("aria-pressed", isActive ? "true" : "false");
	};

	const getLanguageLabel = (code: string): string => {
		const found = MENU_LANGUAGES.find((l) => l.code === code);
		return found?.label ?? code;
	};

	const resolveLanguageCodeFromText = (text: string): string => {
		const raw = (text || "").trim();
		const direct = MENU_LANGUAGES.find(
			(o) =>
				o.code.toLowerCase() === raw.toLowerCase() ||
				o.label.toLowerCase() === raw.toLowerCase(),
		);
		return normalizeMenuLanguage(direct?.code ?? raw);
	};

	const setLanguageDropdownOpen = (isOpen: boolean): void => {
		languageList?.classList.toggle("is-open", isOpen);
		popup?.classList.toggle(LANGUAGE_DROPDOWN_OPEN_CLASS, isOpen);
	};

	const trapScroll = (e: WheelEvent | TouchEvent) => {
		e.stopPropagation();
	};

	const addScrollTrap = (el: HTMLElement) => {
		el.addEventListener("wheel", trapScroll, { passive: true });
		el.addEventListener("touchmove", trapScroll, { passive: true });
	};

	const syncUiFromConfig = (config: MenuConfig) => {
		if (languageInput) {
			languageInput.value = getLanguageLabel(config.language);
			languageInput.dataset.code = config.language;
		}

		if (themeButtons) {
			setActiveToggleButton(themeButtons.light, config.theme === "light");
			setActiveToggleButton(themeButtons.dark, config.theme === "dark");
		}

		if (fontButtons) {
			setActiveToggleButton(fontButtons.sans, config.fontFamily === "sans");
			setActiveToggleButton(fontButtons.serif, config.fontFamily === "serif");
		}
	};

	const maybeRedirectToPreferredLanguage = (): void => {
		const config = readMenuConfig();
		const targetPath = buildLanguageRedirectPath(
			window.location.pathname,
			config.language,
		);
		if (!targetPath) return;

		const current = `${window.location.pathname}`;
		if (targetPath === current) return;

		window.location.replace(
			`${targetPath}${window.location.search}${window.location.hash}`,
		);
	};

	const positionPopup = (p: HTMLDivElement, anchor: HTMLElement) => {
		const padding = 8;
		const anchorRect = anchor.getBoundingClientRect();

		const rect = p.getBoundingClientRect();
		const width = rect.width || 320;
		const height = rect.height || 260;

		let left = anchorRect.left;
		let top = anchorRect.top - padding - height;
		let placedAbove = true;

		if (top < padding) {
			placedAbove = false;
			top = anchorRect.bottom + padding;
		}

		const maxLeft = Math.max(padding, window.innerWidth - width - padding);
		left = Math.min(Math.max(padding, left), maxLeft);

		p.style.left = `${Math.round(left)}px`;
		p.style.top = `${Math.round(top)}px`;

		const originX =
			anchorRect.left > window.innerWidth - width - padding ? "right" : "left";
		const originY = placedAbove ? "bottom" : "top";
		p.style.transformOrigin = `${originX} ${originY}`;
	};

	const close = () => {
		if (!open) return;
		open = false;
		setLanguageDropdownOpen(false);

		document.body.classList.remove("hallv-menu-popup-open");

		const btn = getMenuButton();
		btn?.setAttribute("aria-expanded", "false");

		if (popup) {
			popup.classList.remove("is-open");
			window.setTimeout(() => {
				if (!open && popup) popup.hidden = true;
			}, 140);
		}

		document.removeEventListener("pointerdown", onDocPointerDown, true);
		document.removeEventListener("keydown", onDocKeyDown, true);
		window.removeEventListener("resize", onWindowResize);

		if (restoreFocus) {
			restoreFocus.focus();
			restoreFocus = null;
		}
	};

	const onDocPointerDown = (e: PointerEvent) => {
		if (!open) return;
		const target = e.target as Node | null;
		if (!target) return;

		const btn = getMenuButton();
		if (popup?.contains(target)) return;
		if (btn?.contains(target)) return;

		close();
	};

	const onDocKeyDown = (e: KeyboardEvent) => {
		if (!open) return;
		if (e.key !== "Escape") return;
		e.preventDefault();
		close();
	};

	const onWindowResize = () => {
		if (!open || !popup) return;
		const btn = getMenuButton();
		if (!btn) return;
		positionPopup(popup, btn);
	};

	const renderLanguageOptions = (filterRaw: string) => {
		if (!languageList || !languageInput) return;

		let filterText = (filterRaw || "").trim();

		// If the input currently just shows the selected label (e.g. "English"),
		// open the dropdown with ALL options (so it behaves like a dropdown, not just a filter).
		const selectedCode = languageInput.dataset.code;
		if (
			selectedCode &&
			filterText.toLowerCase() === getLanguageLabel(selectedCode).toLowerCase()
		) {
			filterText = "";
		}

		const filter = filterText.toLowerCase();
		languageList.innerHTML = "";

		const matches = MENU_LANGUAGES.filter((o) => {
			if (!filter) return true;
			return (
				o.label.toLowerCase().includes(filter) ||
				o.code.toLowerCase().includes(filter)
			);
		});

		if (matches.length === 0) {
			const empty = document.createElement("div");
			empty.className = "hallv-menu-option is-empty";
			empty.textContent = "No matches";
			languageList.appendChild(empty);
			return;
		}

		for (const opt of matches) {
			const row = document.createElement("button");
			row.type = "button";
			row.className = "hallv-menu-option";
			row.setAttribute("role", "option");
			row.dataset.code = opt.code;
			row.textContent = `${opt.label} (${opt.code})`;

			// pointerdown to keep focus on the input and avoid blur-closing before click.
			row.addEventListener("pointerdown", (ev) => ev.preventDefault());
			row.addEventListener("click", () => {
				const code = normalizeMenuLanguage(opt.code);
				const current = normalizeMenuLanguage(readMenuConfig().language);

				if (code === current) {
					setLanguageDropdownOpen(false);
					languageInput?.blur();
					return;
				}

				const inputEl = languageInput;
				if (inputEl) {
					inputEl.value = getLanguageLabel(code);
					inputEl.dataset.code = code;
				}

				setLanguageDropdownOpen(false);
				updateMenuConfig({ language: code });
				window.location.reload();
			});

			languageList.appendChild(row);
		}
	};

	const createPopup = (): HTMLDivElement => {
		const p = document.createElement("div");
		p.className = "hallv-menu-popup";
		p.id = "hallv-menu-popup";
		p.hidden = true;

		p.tabIndex = -1;
		p.setAttribute("role", "dialog");
		p.setAttribute("aria-label", "Menu preferences");

		const header = document.createElement("div");
		header.className = "hallv-menu-popup__header";
		header.textContent = "Preferences";
		p.appendChild(header);

		// ===== 1) Language (array-backed searchable dropdown)
		const languageSection = document.createElement("section");
		languageSection.className = "hallv-menu-section";

		const languageTitle = document.createElement("div");
		languageTitle.className = "hallv-menu-section__title";
		languageTitle.textContent = "Language";
		languageSection.appendChild(languageTitle);

		const languageField = document.createElement("div");
		languageField.className = "hallv-menu-field";

		const languageLabel = document.createElement("label");
		languageLabel.className = "hallv-menu-label";
		languageLabel.htmlFor = "hallv-menu-language-input";
		languageLabel.textContent = "Select language";
		languageField.appendChild(languageLabel);

		const inputWrap = document.createElement("div");
		inputWrap.className = "hallv-menu-select";

		const input = document.createElement("input");
		languageInput = input;
		input.id = "hallv-menu-language-input";
		input.className = "hallv-menu-input";
		input.type = "text";
		input.autocomplete = "off";
		input.spellcheck = false;
		input.placeholder = "Type to search…";
		input.setAttribute("aria-haspopup", "listbox");
		input.setAttribute("aria-controls", "hallv-menu-language-options");
		inputWrap.appendChild(input);

		const options = document.createElement("div");
		languageList = options;
		options.id = "hallv-menu-language-options";
		options.className = "hallv-menu-options";
		options.setAttribute("role", "listbox");
		addScrollTrap(options);
		inputWrap.appendChild(options);

		input.addEventListener("focus", () => {
			// Show all options on focus (dropdown behavior), then user can type to filter.
			input.select();
			renderLanguageOptions("");
			setLanguageDropdownOpen(true);
		});

		input.addEventListener("input", () => {
			renderLanguageOptions(input.value);
			setLanguageDropdownOpen(true);
		});

		input.addEventListener("keydown", (e) => {
			if (e.key !== "Enter") return;
			e.preventDefault();

			const code = resolveLanguageCodeFromText(input.value);
			const current = normalizeMenuLanguage(readMenuConfig().language);

			// If user selects the current language, do nothing.
			if (code === current) {
				setLanguageDropdownOpen(false);
				input.blur();
				return;
			}

			input.value = getLanguageLabel(code);
			input.dataset.code = code;
			setLanguageDropdownOpen(false);

			updateMenuConfig({ language: code });
			window.location.reload();
		});

		input.addEventListener("blur", () => {
			// Delay so clicking an option (pointerdown) can run first.
			window.setTimeout(() => {
				if (!options.isConnected) return;
				const active = document.activeElement;
				if (active && options.contains(active)) return;
				setLanguageDropdownOpen(false);
			}, 0);
		});

		languageField.appendChild(inputWrap);
		languageSection.appendChild(languageField);
		p.appendChild(languageSection);

		// ===== 2) Theme
		const themeSection = document.createElement("section");
		themeSection.className = "hallv-menu-section";

		const themeTitle = document.createElement("div");
		themeTitle.className = "hallv-menu-section__title";
		themeTitle.textContent = "Theme";
		themeSection.appendChild(themeTitle);

		const themeTabs = document.createElement("div");
		themeTabs.className = "hallv-menu-tabs";
		themeTabs.setAttribute("role", "group");
		themeTabs.setAttribute("aria-label", "Theme");

		const themeLight = document.createElement("button");
		themeLight.type = "button";
		themeLight.className = "hallv-menu-tab";
		themeLight.textContent = "☀️ Light";

		const themeDark = document.createElement("button");
		themeDark.type = "button";
		themeDark.className = "hallv-menu-tab";
		themeDark.textContent = "🌙 Dark";

		themeButtons = { light: themeLight, dark: themeDark };

		themeLight.addEventListener("click", () => {
			applyTheme("light");
			syncUiFromConfig(readMenuConfig());
		});
		themeDark.addEventListener("click", () => {
			applyTheme("dark");
			syncUiFromConfig(readMenuConfig());
		});

		themeTabs.appendChild(themeLight);
		themeTabs.appendChild(themeDark);
		themeSection.appendChild(themeTabs);
		p.appendChild(themeSection);

		// ===== 3) Font family
		const fontSection = document.createElement("section");
		fontSection.className = "hallv-menu-section";

		const fontTitle = document.createElement("div");
		fontTitle.className = "hallv-menu-section__title";
		fontTitle.textContent = "Font family";
		fontSection.appendChild(fontTitle);

		const fontTabs = document.createElement("div");
		fontTabs.className = "hallv-menu-tabs";
		fontTabs.setAttribute("role", "group");
		fontTabs.setAttribute("aria-label", "Font family");

		const fontSans = document.createElement("button");
		fontSans.type = "button";
		fontSans.className = "hallv-menu-tab";
		fontSans.textContent = "Sans";

		const fontSerif = document.createElement("button");
		fontSerif.type = "button";
		fontSerif.className = "hallv-menu-tab";
		fontSerif.textContent = "Serif";

		fontButtons = { sans: fontSans, serif: fontSerif };

		const applyFontFamily = (fontFamily: MenuFontFamily) => {
			const next = updateMenuConfig({ fontFamily });
			applyMenuConfigToDom(next);
			syncUiFromConfig(next);
		};

		fontSans.addEventListener("click", () => applyFontFamily("sans"));
		fontSerif.addEventListener("click", () => applyFontFamily("serif"));

		fontTabs.appendChild(fontSans);
		fontTabs.appendChild(fontSerif);
		fontSection.appendChild(fontTabs);
		p.appendChild(fontSection);

		// ===== 4) Audio
		const audioSection = document.createElement("section");
		audioSection.className = "hallv-menu-section";

		const audioTitle = document.createElement("div");
		audioTitle.className = "hallv-menu-section__title";
		audioTitle.textContent = "Audio";
		audioSection.appendChild(audioTitle);

		const audioHint = document.createElement("div");
		audioHint.className = "hallv-menu-hint";
		audioHint.textContent = "Audio version of all the books";
		audioSection.appendChild(audioHint);

		const audioLink = document.createElement("a");
		audioLink.className = "hallv-menu-tab hallv-menu-tab--link";
		audioLink.href = "/audio-library.html";
		audioLink.textContent = "🎧 Audio Library";
		audioSection.appendChild(audioLink);
		p.appendChild(audioSection);

		addScrollTrap(p);

		return p;
	};

	const openPopup = () => {
		if (open) return;

		const btn = getMenuButton();
		if (!btn) return;
		restoreFocus = (document.activeElement as HTMLElement) ?? null;

		open = true;
		document.body.classList.add("hallv-menu-popup-open");
		btn.setAttribute("aria-expanded", "true");

		const p = popup ?? createPopup();
		popup = p;

		if (!p.isConnected) document.body.appendChild(p);

		p.hidden = false;
		p.classList.remove("is-open");

		const config = readMenuConfig();
		syncUiFromConfig(config);

		p.style.visibility = "hidden";
		p.style.left = "0px";
		p.style.top = "0px";
		void p.getBoundingClientRect();

		positionPopup(p, btn);
		p.style.visibility = "visible";

		requestAnimationFrame(() => {
			if (!open) return;
			p.classList.add("is-open");
		});


		// Do not auto-focus any control on open.

		document.addEventListener("pointerdown", onDocPointerDown, true);
		document.addEventListener("keydown", onDocKeyDown, true);
		window.addEventListener("resize", onWindowResize);
	};

	const toggle = () => {
		if (open) close();
		else openPopup();
	};

	return {
		toggle,
		close,
		isOpen: () => open,
	};
}
